import { renderTemplate } from "../templateRenderer";
import { CLASSIC_TEMPLATE, CLASSIC_CSS } from "./loader";
import type { InvoiceTemplateContext } from "../types";

const CLASSIC_PAGE_SIZES = {
  A4: { width: "210mm", pad: "18mm", font: "13px" },
  A3: { width: "297mm", pad: "24mm", font: "15px" },
  "80mm": { width: "72mm", pad: "4mm", font: "11.5px" },
} as const;

const BASE_TEMPLATE = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8"/>
  <meta name="viewport" content="width=device-width,initial-scale=1"/>
  <title>{{ title }}</title>
  <style>{{ styles }}</style>
</head>
<body>
  {{ content | safe }}
</body>
</html>`;

function buildCssVariables(ctx: InvoiceTemplateContext): string {
  const { pageSize, isForPrint } = ctx;
  const is80 = pageSize === "80mm";
  const sz = CLASSIC_PAGE_SIZES[pageSize];

  return `
    :root {
      --inv-font:       ${sz.font};
      --inv-width:      ${sz.width};
      --inv-pad:        ${isForPrint ? (is80 ? "0" : "12mm") : sz.pad};
      --inv-bg:         ${isForPrint ? "#fff" : "#e5e7eb"};
      --inv-body-pad:   ${isForPrint ? "0" : is80 ? "8px 4px" : "24px 16px"};
      --inv-shadow:     ${!isForPrint ? "0 4px 16px rgba(0,0,0,0.10)" : "none"};
      --inv-radius:     ${!isForPrint ? "4px" : "0"};
      --inv-th-pad:     ${is80 ? "4px 2px" : "7px 6px"};
      --inv-td-pad:     ${is80 ? "4px 2px" : "7px 6px"};
      --inv-h2-size:    ${is80 ? "1.15em" : "1.3em"};
      --inv-p-size:     ${is80 ? "0.82em" : "0.9em"};
      --inv-grand-size: ${is80 ? "1em" : "1.1em"};
      --inv-ftr-mt:     ${is80 ? "12px" : "24px"};
      --inv-ftr-size:   ${is80 ? "0.78em" : "0.82em"};
      --inv-page-size:  ${is80 ? "80mm auto" : pageSize};
    }
  `;
}

export function classicTemplate(ctx: InvoiceTemplateContext): string {
  const { order, config, fmt } = ctx;

  const currency = config.currency || "LKR";
  const items = order.items || [];
  const payments = order.payments || [];
  const dateObj = new Date(order.order_date || order.created_at);

  const contentContext = {
    storeName: config.store_name || "POS System",
    address: config.address || "",
    phone: config.phone || "",
    footer: config.receipt_footer || "Thank you for your business!",
    customerName: order.customer_name || "Walk-in Customer",
    customerPhone: order.customer_phone || "",
    customerAddress: order.customer_address || "",
    orderNo: order.order_no || "",
    dateStr: dateObj.toLocaleDateString(),
    timeStr: dateObj.toLocaleTimeString(),
    sellingType: order.selling_type === "wholesale" ? "Wholesale" : "Retail",
    currency,
    items,
    payments,
    subtotal: order.subtotal,
    grandTotal: order.grand_total,
    paidAmount: order.paid_amount,
    dueAmount: order.due_amount,
    discountAmount: order.discount_amount,
    exchangeAmount: order.exchange_amount,
    otherCharges: order.other_charges,
    showDisc: parseFloat(order.discount_amount) > 0,
    showExch: parseFloat(order.exchange_amount) > 0,
    showChg: parseFloat(order.other_charges) > 0,
    hasDue: parseFloat(order.due_amount) > 0,
    fmt,
  };

  const content = renderTemplate(CLASSIC_TEMPLATE, contentContext);
  const styles = buildCssVariables(ctx) + "\n" + CLASSIC_CSS;

  return renderTemplate(BASE_TEMPLATE, {
    title: `Invoice - ${order.order_no}`,
    styles,
    content,
  });
}
